import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import FpPartnersService from '@utility/services/fp-partners';
import ShippingLabelsCarriersService from '@utility/services/shipping-labels-carriers';
import StorageProviderService from '@utility/services/storage-provider';
import { CommonConstant } from '@utility/constants/common';
import { ToastMessage } from '@utility/toast';
import type { WmsWarehouse } from '../components/add-provider/ProviderTypeStep';
import type { ProviderCarrier } from '../lib/carriers';
import { providerKeys } from '../lib/query-keys';
import {
  type PagedFilter,
  pagedFilter,
  toListParams,
  usePagedFilter,
} from './use-paged-filter';

type Page<T> = { rows: T[]; total: number };

/* What a paged table needs from its hook: the rows, the filter and its setters. */
function pagedResult<T>(
  query: { data?: Page<T>; isFetching: boolean; error: Error | null },
  applied: unknown,
) {
  return {
    data: query.data?.rows ?? [],
    totalData: query.data?.total ?? 0,
    loading: !applied || query.isFetching,
    error: query.error?.message ?? '',
  };
}

const inventoryFilter: PagedFilter = {
  ...pagedFilter({ available: CommonConstant.sortDirection.descending }),
  title: '',
};

/**
 * The provider's stock with each item's SCM data. Items without stock are
 * hidden until asked for; a first answer with none in stock shows them all.
 */
export function useProviderInventory(id: string) {
  const [showZeroInventory, setShowZeroInventory] = useState(false);
  const paged = usePagedFilter(inventoryFilter, (filter: PagedFilter) => ({
    ...toListParams(filter),
    inventory: showZeroInventory ? '' : 'yes',
  }));
  const { applied } = paged;

  const query = useQuery({
    queryKey: providerKeys.inventory(id, applied),
    queryFn: async (): Promise<Page<Record<string, unknown>>> => {
      const result = await StorageProviderService.inventoryList(id, applied);
      const rows: Record<string, unknown>[] = result?.data ?? [];
      const itemIds = rows.map(row => row.item_id);
      if (!itemIds.length) return { rows, total: result?.total_data ?? 0 };
      const scm = await StorageProviderService.scmDataProviderItems({
        items: itemIds,
      });
      const scmRows: Record<string, unknown>[] = scm?.data ?? [];
      return {
        rows: rows.map(row => ({
          ...row,
          scm: scmRows.find(item => item.item_id === row.item_id) ?? {},
        })),
        total: result?.total_data ?? 0,
      };
    },
    enabled: !!id && !!applied,
    placeholderData: keepPreviousData,
  });

  const firstAnswer = useRef(true);
  useEffect(() => {
    if (!query.data || query.isPlaceholderData || !firstAnswer.current) return;
    firstAnswer.current = false;
    if (!query.data.rows.length && !showZeroInventory)
      setShowZeroInventory(true);
  }, [query.data, query.isPlaceholderData, showZeroInventory]);

  return {
    ...pagedResult(query, applied),
    ...paged,
    refresh: async () => {
      await query.refetch();
    },
    showZeroInventory,
    setShowZeroInventory,
    search: paged.filter.search,
    setSearch: (search: string) => paged.filterChanged('search', search),
  };
}

/** Channel listings the provider holds that match no catalog item. */
export function useUnmatchedAliases(id: string, search: string) {
  const paged = usePagedFilter(
    pagedFilter(),
    filter => ({ ...filter, search }),
    'getUnmatchedSkuList',
  );
  const { applied } = paged;

  const query = useQuery({
    queryKey: providerKeys.unmatched(id, applied),
    queryFn: async () => {
      try {
        const result = await StorageProviderService.fetchUnmatchedSkuList(
          id,
          applied,
        );
        return {
          rows: result?.data?.data ?? [],
          total: result?.data?.total_data ?? 0,
        } as Page<unknown>;
      } catch (error) {
        ToastMessage.apiError(error);
        throw error;
      }
    },
    enabled: !!id && !!applied,
    placeholderData: keepPreviousData,
  });

  return {
    ...pagedResult(query, applied),
    ...paged,
    refresh: async () => {
      await query.refetch();
    },
  };
}

/** What the WMS provider charges: billed services and packaging materials. */
export function useRateCard(id: string) {
  const rates = useQuery({
    queryKey: providerKeys.rates(id),
    queryFn: async () =>
      (await StorageProviderService.wmsProviderRate(id)).data,
    enabled: !!id,
  });
  const materials = useQuery({
    queryKey: providerKeys.materials(id),
    queryFn: async () =>
      (await StorageProviderService.wmsProviderMaterial(id)).data,
    enabled: !!id,
  });
  return {
    rates: rates.data ?? {},
    ratesLoading: rates.isPending,
    materials: materials.data ?? [],
    materialsLoading: materials.isPending,
  };
}

const invoiceFilter = pagedFilter({
  total_items: CommonConstant.sortDirection.descending,
});

/** The WMS provider's invoices to its customers. */
export function useProviderInvoices(id: string) {
  const paged = usePagedFilter(invoiceFilter, toListParams);
  const { applied } = paged;

  const query = useQuery({
    queryKey: providerKeys.invoices(id, applied),
    queryFn: async () => {
      const result = await StorageProviderService.wmsProviderInvoices(
        id,
        applied,
      );
      return {
        rows: result?.data?.invoices ?? [],
        total: result?.data?.total ?? 0,
      } as Page<unknown>;
    },
    enabled: !!id && !!applied,
    placeholderData: keepPreviousData,
  });

  return { ...pagedResult(query, applied), ...paged };
}

/** Carrier accounts the provider ships with (manual ones are not listed). */
export function useProviderCarriers(id: string) {
  const queryClient = useQueryClient();
  const key = providerKeys.carriers(id);
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const result = await ShippingLabelsCarriersService.get({
        fulfillment_provider_id: id,
      });
      return ((result?.data ?? []) as ProviderCarrier[])
        .filter(carrier => !carrier.manual)
        .map(carrier => ({ ...carrier, _id: Math.random() }));
    },
    enabled: !!id,
  });

  return {
    data: query.data ?? [],
    loading: query.isFetching,
    refresh: async () => {
      await query.refetch();
    },
    /* The editor hands back the saved carrier; it replaces the one shown. */
    replace: (saved: ProviderCarrier) =>
      queryClient.setQueryData<ProviderCarrier[]>(key, carriers =>
        carriers?.map(carrier =>
          carrier.provider.value === saved.provider.value ? saved : carrier,
        ),
      ),
  };
}

/** Skupreme WMS warehouses a new provider can connect to. */
export function useWmsWarehouses() {
  const query = useQuery({
    queryKey: providerKeys.wmsWarehouses(),
    queryFn: async () =>
      ((await FpPartnersService.getWMSwarehouses())?.warehouses ??
        []) as WmsWarehouse[],
  });
  return { data: query.data ?? [] };
}
